import moment from 'moment'
import { resolveEngineScheduleStudyDay } from '../engine/anchorStudyDay'
import { classifyScheduleEntry, SCHEDULE_SHAPE } from '../classifyScheduleEntry'
import { clampStudyDay } from '../constants'
import { getVisitChainedStudyDay } from './visitChainStudyDay'
import {
  getInstrumentChainedStudyDay,
  getInstrumentChainTriggerIds,
  isVisitChainedSchedule,
} from './instrumentChainStudyDay'

export const resolveLegacyDeployStudyDay = (instrument, schedule, context) => {
  const {
    visits,
    selectedArmVisitIds,
    useAbsoluteStartDate,
    absoluteStartDate,
    armsForEngine,
    resolveInstrumentStudyDay,
    projectionMode,
    participantVisits,
  } = context

  if (classifyScheduleEntry(schedule) === SCHEDULE_SHAPE.v3) {
    return resolveEngineScheduleStudyDay(schedule, {
      useAbsoluteStartDate,
      absoluteStartDate,
      visits,
      arms: armsForEngine,
      selectedArmVisitIds,
      resolveInstrumentStudyDay,
      projectionMode,
      participantVisits,
    })
  }

  if (isVisitChainedSchedule(schedule)) {
    return getVisitChainedStudyDay(schedule, visits, selectedArmVisitIds, {
      projectionMode,
      participantVisits,
      useAbsoluteStartDate,
      absoluteStartDate,
    })
  }

  const triggerIds = getInstrumentChainTriggerIds(schedule)
  if (triggerIds.length) {
    const parentStudyDays = new Map()
    triggerIds.forEach(triggerId => {
      const parentDay = resolveInstrumentStudyDay(triggerId)
      if (parentDay != null) parentStudyDays.set(String(triggerId), parentDay)
    })
    return getInstrumentChainedStudyDay(schedule, parentStudyDays)
  }

  if (useAbsoluteStartDate && absoluteStartDate && !schedule?.deploy?.first_login) {
    const startDate = moment.utc(absoluteStartDate).startOf('day')
    if (schedule?.deploy?.absolute) {
      const epochSeconds = Number(schedule.deploy.absolute)
      const absoluteDate = Number.isFinite(epochSeconds)
        ? moment.utc(epochSeconds * 1000).startOf('day')
        : null
      if (absoluteDate?.isValid() && startDate.isValid()) {
        const day = absoluteDate.diff(startDate, 'days') + 1
        return day < 1 ? null : day
      }
    } else if (instrument.deployed) {
      const deployedDate = moment.utc(instrument.deployed).startOf('day')
      if (deployedDate.isValid() && startDate.isValid()) {
        return Math.max(1, deployedDate.diff(startDate, 'days') + 1)
      }
    }
  }

  const firstLogin = schedule?.deploy?.first_login
  if (firstLogin && firstLogin.interval != null) {
    const interval = Number(firstLogin.interval)
    const projectedDay = Number.isFinite(interval) ? 1 + interval : 1
    return clampStudyDay(projectedDay) ?? projectedDay
  }

  if (schedule?.deploy?.days_offset != null && schedule.deploy.days_offset !== '') {
    const offset = Number(schedule.deploy.days_offset)
    const projectedDay = Number.isFinite(offset) ? offset : schedule.deploy.days_offset
    return clampStudyDay(projectedDay) ?? projectedDay
  }

  if (schedule?.deploy?.now !== undefined) {
    if (instrument.deployed && useAbsoluteStartDate && absoluteStartDate) {
      const startDate = moment.utc(absoluteStartDate).startOf('day')
      const deployedDate = moment.utc(instrument.deployed).startOf('day')
      if (deployedDate.isValid() && startDate.isValid()) {
        return Math.max(1, deployedDate.diff(startDate, 'days') + 1)
      }
    }
    const visitDays = visits
      .map(v =>
        v.metadata?.computed_study_day !== undefined
          ? v.metadata.computed_study_day
          : v.day,
      )
      .filter(day => day != null)
    return visitDays.length ? Math.min(...visitDays) : 1
  }

  if (schedule?.deploy?.absolute != null && (!useAbsoluteStartDate || !absoluteStartDate)) {
    return 1
  }
  return null
}
